// useSettingsMembers.js

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    removeBaseMember,
    setBaseMemberRole,
    subscribeToBaseMembers,
} from 'services/bases/BaseMemberService';
import { subscribeToBaseCustomRoles } from 'services/bases/BaseCustomRoleService';
import {
    formatRoleLabel,
    BASE_ROLES,
} from 'services/bases/BaseRoleService';
import { isBuiltinRole, resolveMemberAccess } from 'security/rbac';

function roleWeight(roleKey, tier) {
    if (tier === BASE_ROLES.OWNER || roleKey === BASE_ROLES.OWNER) return 0;
    if (tier === BASE_ROLES.ADMIN || roleKey === BASE_ROLES.ADMIN) return 1;
    return 2;
}

const useSettingsMembers = ({
    isOpen,
    baseKey,
    base,
    baseRole,
    baseTier,
    userKey,
    canChangeRoles,
    canDeleteMembers,
}) => {
    const [members, setMembers] = useState([]);
    const [customRoles, setCustomRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    useEffect(() => {
        if (!isOpen || !baseKey) {
            setMembers([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const unsubscribe = subscribeToBaseMembers(
            baseKey,
            (rows) => {
                setMembers(Array.isArray(rows) ? rows : []);
                setLoading(false);
            },
            (error) => {
                setErrorMessage(error?.message || 'Failed to load members.');
                setLoading(false);
            }
        );

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [isOpen, baseKey]);

    useEffect(() => {
        if (!isOpen || !baseKey) {
            setCustomRoles([]);
            return undefined;
        }

        return subscribeToBaseCustomRoles(
            baseKey,
            (rows) => setCustomRoles(Array.isArray(rows) ? rows : []),
            () => setCustomRoles([])
        );
    }, [isOpen, baseKey]);

    const customRolesByKey = useMemo(() => {
        return customRoles.reduce((acc, role) => {
            if (role?.key) acc[role.key] = role;
            return acc;
        }, {});
    }, [customRoles]);

    const roleOptions = useMemo(() => {
        const options = [
            { value: BASE_ROLES.MEMBER, label: formatRoleLabel(BASE_ROLES.MEMBER, customRolesByKey) },
            { value: BASE_ROLES.ADMIN, label: formatRoleLabel(BASE_ROLES.ADMIN, customRolesByKey) },
        ];
        customRoles.forEach((role) => {
            if (!role?.key) return;
            options.push({
                value: role.key,
                label: formatRoleLabel(role.key, customRolesByKey),
            });
        });
        return options;
    }, [customRoles, customRolesByKey]);

    const sortedMembers = useMemo(() => {
        return [...members].sort((a, b) => {
            const aAccess = resolveMemberAccess({
                ownerKey: base?.ownerKey,
                userKey: a?.userKey || a?.key,
                memberRole: a?.role,
                memberBaseRole: a?.baseRole,
            });
            const bAccess = resolveMemberAccess({
                ownerKey: base?.ownerKey,
                userKey: b?.userKey || b?.key,
                memberRole: b?.role,
                memberBaseRole: b?.baseRole,
            });
            const roleDelta = roleWeight(aAccess?.roleKey, aAccess?.tier)
                - roleWeight(bAccess?.roleKey, bAccess?.tier);
            if (roleDelta !== 0) return roleDelta;

            const aName = String(a?.title || a?.email || a?.userKey || '').toLowerCase();
            const bName = String(b?.title || b?.email || b?.userKey || '').toLowerCase();
            return aName.localeCompare(bName);
        });
    }, [members, base?.ownerKey]);

    const subtitle = loading
        ? 'Loading members...'
        : `${members.length} member${members.length === 1 ? '' : 's'}`;

    const canManageTarget = useCallback((member, action) => {
        const targetUserKey = member?.userKey || member?.key;
        const targetAccess = resolveMemberAccess({
            ownerKey: base?.ownerKey,
            userKey: targetUserKey,
            memberRole: member?.role,
            memberBaseRole: member?.baseRole,
        });
        const targetTier = targetAccess?.tier;
        const allowedByAction = action === 'changeRole' ? canChangeRoles : canDeleteMembers;
        const actorTier = baseTier || baseRole;

        if (!allowedByAction) return false;
        if (!targetUserKey || targetUserKey === userKey) return false;
        if (targetTier === BASE_ROLES.OWNER) return false;

        if (actorTier === BASE_ROLES.ADMIN && targetTier === BASE_ROLES.ADMIN) {
            return false;
        }

        return true;
    }, [canChangeRoles, canDeleteMembers, userKey, base?.ownerKey, baseRole, baseTier]);

    const handleRoleChange = useCallback(async (member, nextRole) => {
        if (!baseKey || !canManageTarget(member, 'changeRole')) return;

        const targetUserKey = member?.userKey || member?.key;
        const role = String(nextRole || '').trim();
        if (!targetUserKey || role === BASE_ROLES.OWNER) return;
        if (!isBuiltinRole(role) && !customRolesByKey[role]) return;

        try {
            setErrorMessage('');
            await setBaseMemberRole(baseKey, targetUserKey, role);
            setStatusMessage('Role updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update role.');
        }
    }, [canManageTarget, customRolesByKey, baseKey]);

    const handleRemoveMember = useCallback(async (member) => {
        if (!baseKey || !canManageTarget(member, 'remove')) return;

        const targetUserKey = member?.userKey || member?.key;
        if (!targetUserKey) return;

        try {
            setErrorMessage('');
            await removeBaseMember(baseKey, targetUserKey);
            setStatusMessage('Member removed.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to remove member.');
        }
    }, [canManageTarget, baseKey]);

    const clearMessages = useCallback(() => {
        setErrorMessage('');
        setStatusMessage('');
    }, []);

    const getMemberRoleLabel = useCallback((member) => {
        return formatRoleLabel(member?.role, customRolesByKey);
    }, [customRolesByKey]);

    return {
        members,
        sortedMembers,
        subtitle,
        loading,
        errorMessage,
        statusMessage,
        clearMessages,
        canManageTarget,
        handleRoleChange,
        handleRemoveMember,
        roleOptions,
        customRolesByKey,
        getMemberRoleLabel,
    };
};

export default useSettingsMembers;
